import { TermsRuleContract } from 'ApiClient/royalty'
import {
    AddRoyaltyAdvance,
    AgreementForm,
    AgreementSearch,
    CreateAgreement,
    EditRoyaltyAdvance,
    RoyaltyAdvance,
} from 'Royalty/utils/royaltyTypes'

import { servicesAsync } from 'Common/environment/environment'
import { SalesTransactionContract } from 'Common/types'
import { TermsRuleSet } from 'Common/types'
import { fetchDelete, fetchGet, fetchPost, fetchPut } from 'Common/utils/query/request'

const baseUrl = servicesAsync.royalty('/v1/RoyaltyAgreements/')
const baseUrlV2 = servicesAsync.royalty('/v2/RoyaltyAgreements/')

interface CreateTermsRuleSetRequest {
    agreementId: number
    termsRule: TermsRuleContract
}

interface AddAdvanceWithAgreementId {
    id: number
    advance: AddRoyaltyAdvance
}

interface EditAdvanceWithAgreementId {
    id: number
    advance: EditRoyaltyAdvance
}

interface AdvanceIdWithAgreementId {
    agreementId: number
    advanceId: number
}

interface AgreementWithForceUpdate {
    agreementId: number
    agreement: AgreementForm
    forceUpdate: boolean
}

export const getAgreement = async (id: number) => {
    return await fetchGet(`${await baseUrl}${id}`)
}

export const agreementsActiveGet = async () => {
    const result = await fetchGet(`${await baseUrl}active`)
    return result as AgreementSearch[]
}

export const agreementsInactiveGet = async () => {
    const result = await fetchGet(`${await baseUrl}inactive`)
    return result as AgreementSearch[]
}

export const postAgreement = async (agreement: CreateAgreement) => {
    return await fetchPost(await baseUrl, JSON.stringify(agreement))
}

export const putAgreement = async (agreementWithForceUpdate: AgreementWithForceUpdate) => {
    return await fetchPut(
        `${await baseUrlV2}${agreementWithForceUpdate.agreementId}?forceUpdate=${agreementWithForceUpdate.forceUpdate}`,
        JSON.stringify(agreementWithForceUpdate.agreement),
    )
}

export const deleteAgreement = async (id: number) => {
    return await fetchDelete(`${await baseUrl}${id}`)
}

export const getUnsettledSalesTransactions = async (
    agreementId: string,
): Promise<SalesTransactionContract[]> => {
    return await fetchGet(`${await baseUrl}${agreementId}/unsettledSalesTransactions`)
}

export const activateAgreement = async (id: number) => {
    return await fetchPost(`${await baseUrl}${id}/activate`, '')
}

export const deactivateAgreement = async (id: number) => {
    return await fetchPost(`${await baseUrl}${id}/deactivate`, '')
}

export const getAgreementAdvance = async (advanceIdWithAgreementId: AdvanceIdWithAgreementId) => {
    return await fetchGet(
        `${await baseUrl}${advanceIdWithAgreementId.agreementId}/advances/${advanceIdWithAgreementId.advanceId}`,
    )
}

export const getAdvances = async (id: number) => {
    const response = await fetchGet(`${await baseUrl}${id}/advances`)
    return response as RoyaltyAdvance[]
}

export const postAdvance = async (advanceWithAgreementId: AddAdvanceWithAgreementId) => {
    const response = await fetchPost(
        `${await baseUrl}${advanceWithAgreementId.id}/advances`,
        JSON.stringify(advanceWithAgreementId.advance),
    )

    return response
}

export const putAdvance = async (advanceWithAgreementId: EditAdvanceWithAgreementId) => {
    const response = await fetchPut(
        `${await baseUrl}${advanceWithAgreementId.id}/advances/${advanceWithAgreementId.advance.id}`,
        JSON.stringify(advanceWithAgreementId.advance),
    )

    return response
}

export const deleteAdvance = async (advanceIdWithAgreementId: AdvanceIdWithAgreementId) => {
    return await fetchDelete(
        `${await baseUrl}${advanceIdWithAgreementId.agreementId}/advances/${advanceIdWithAgreementId.advanceId}`,
    )
}

export const getAgreementTermsRuleSets = async (agreementId: number) => {
    const response = await fetchGet(`${await baseUrl}${agreementId}/TermsRuleSets`)
    return response as TermsRuleSet[]
}

export const postAgreementTermsRuleSet = async (request: CreateTermsRuleSetRequest) => {
    return await fetchPost(
        `${await baseUrl}${request.agreementId}/TermsRuleSets`,
        JSON.stringify(request.termsRule),
    )
}
